import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight, FaSpellCheck, FaLanguage, FaDownload, FaRedo } from 'react-icons/fa';
import TypoResultModal from './TypoResultModal';
import { SUPPORTED_LANGUAGES } from '../../config';

// Progress 상태를 i18n 메시지로 변환하는 helper
const getProgressMessage = (t, status, percent) => {
  switch (status) {
    case 'preparing':
      return t('progress.preparing');
    case 'checking':
      return t('progress.checking', { percent: percent || 0 });
    case 'saving':
      return t('progress.saving');
    case 'completed':
      return t('progress.completed');
    default:
      return t('typoViewer.checking');
  }
};

const TypoTranslateViewer = ({
  tabType, // 'typo' | 'translate'
  slides,
  currentSlideIndex,
  isSlideLoading,
  goToPrevSlide,
  goToNextSlide,
  goToSlide,
  // Typo check related props
  checkSlideTypos,
  checkAllSlidesTypos,
  checkAllProgress,
  isTypoAnalyzingAll,  // 전체 슬라이드 검사 진행 중
  isTypoAnySingleSlideChecking,  // 개별 슬라이드 중 하나라도 검사 중인지
  slideTypoResults,
  slideTypoCheckStatus,
  selectedSlideTypos,
  toggleSlideTypoSelection,
  editedSuggestions,
  updateEditedSuggestion,
  getEditedSuggestion,
  applyingTypoCorrections,
  applySlideTypoCorrections,
  pdfUrl,  // PDF URL for typo result modal
  pdfDoc,  // Cached PDF document object
  setPdfDoc,  // Function to cache PDF document
  // Translate related props
  isTranslating,
  translateProgress,
  translatedLanguages,  // 번역된 언어 목록
  translatePPT,
  downloadTranslatedPPT,
  // 모바일 여부
  isMobile = false,
  // 오타 결과 모달 상태 (외부에서 제어)
  showTypoResultModal,
  setShowTypoResultModal,
  selectedPpt,
  // 오타 위치 매칭용 슬라이드 크기
  typoSlideSize,
  // 오타 임시저장 콜백
  onSaveTypoDraft,
  // 오타 draft 존재 여부
  hasTypoDraft,
  // 오타 draft 적용/초기화 함수
  loadedTypoDraft,
  applyTypoDraft,
  clearTypoDraft,
  // 알림 모달
  showAlertModal: _showAlertModal, // eslint-disable-line no-unused-vars
  showConfirmModal
}) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // 슬라이드 번호 입력 상태
  const [isEditingSlideNumber, setIsEditingSlideNumber] = useState(false);
  const [slideInputValue, setSlideInputValue] = useState('');
  const [slideInputError, setSlideInputError] = useState('');

  const isTypoMode = tabType === 'typo';

  // 모달 열 때의 초기 상태 저장 (닫을 때 비교용)
  const typoInitialStateRef = useRef({ selectedTypos: {}, editedSuggestions: {} });

  // 두 객체가 동일한지 비교하는 헬퍼 함수
  const isObjectEqual = useCallback((obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }, []);

  // 빈 배열을 제거한 selectedTypos 반환 (실제 선택된 것만 비교용)
  const filterEmptySelections = useCallback((selectedTypos) => {
    if (!selectedTypos) return {};
    const filtered = {};
    Object.entries(selectedTypos).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        filtered[key] = value;
      }
    });
    return filtered;
  }, []);

  // 오타 draft에서 선택된 오타 개수 계산
  const countTypoDraftSelections = useCallback((draft) => {
    if (!draft?.selected_typos) return 0;
    return Object.values(draft.selected_typos).reduce((total, typoIndices) => {
      return total + (Array.isArray(typoIndices) ? typoIndices.length : 0);
    }, 0);
  }, []);

  // 오타 결과 모달 열기 핸들러 (draft 확인 모달 포함)
  const handleOpenTypoResultModal = useCallback(async () => {
    setShowTypoResultModal(true);
    // Draft가 있으면 확인 모달 표시
    if (hasTypoDraft && loadedTypoDraft) {
      const draftCount = countTypoDraftSelections(loadedTypoDraft);
      const confirmed = await showConfirmModal?.(t('draft.typoDraftLoaded', { count: draftCount }));
      if (confirmed) {
        // 확인: draft 적용
        applyTypoDraft?.();
        // 초기 상태를 draft로 설정
        typoInitialStateRef.current = {
          selectedTypos: JSON.parse(JSON.stringify(loadedTypoDraft.selected_typos || {})),
          editedSuggestions: JSON.parse(JSON.stringify(loadedTypoDraft.edited_suggestions || {}))
        };
      } else {
        // 취소: draft 무시, 빈 상태로 시작
        typoInitialStateRef.current = { selectedTypos: {}, editedSuggestions: {} };
      }
      // 확인/취소 상관없이 draft 상태 초기화 (다시 물어보지 않음)
      clearTypoDraft?.();
    } else {
      // draft가 없으면 현재 상태를 초기 상태로 저장
      typoInitialStateRef.current = {
        selectedTypos: JSON.parse(JSON.stringify(selectedSlideTypos || {})),
        editedSuggestions: JSON.parse(JSON.stringify(editedSuggestions || {}))
      };
    }
  }, [setShowTypoResultModal, hasTypoDraft, loadedTypoDraft, showConfirmModal, applyTypoDraft, clearTypoDraft, t, countTypoDraftSelections, selectedSlideTypos, editedSuggestions]);

  // 오타 결과 모달 닫기 핸들러 (저장 여부 확인)
  const handleCloseTypoResultModal = useCallback(async () => {
    // 초기 상태와 현재 상태 비교 (빈 배열은 제외)
    const filteredInitialTypos = filterEmptySelections(typoInitialStateRef.current.selectedTypos);
    const filteredCurrentTypos = filterEmptySelections(selectedSlideTypos);

    const hasSelectedChanges = !isObjectEqual(filteredInitialTypos, filteredCurrentTypos);
    const hasEditedChanges = !isObjectEqual(typoInitialStateRef.current.editedSuggestions, editedSuggestions || {});
    const hasChanges = hasSelectedChanges || hasEditedChanges;

    if (hasChanges) {
      const shouldSave = await showConfirmModal?.(t('draft.unsavedChanges'));
      if (shouldSave) {
        // 임시저장 실행
        await onSaveTypoDraft?.();
        // 저장 후 초기 상태 업데이트
        typoInitialStateRef.current = {
          selectedTypos: JSON.parse(JSON.stringify(selectedSlideTypos || {})),
          editedSuggestions: JSON.parse(JSON.stringify(editedSuggestions || {}))
        };
      }
    }

    setShowTypoResultModal(false);
  }, [selectedSlideTypos, editedSuggestions, isObjectEqual, filterEmptySelections, showConfirmModal, t, onSaveTypoDraft, setShowTypoResultModal]);

  // 임시저장 래핑 함수 (저장 성공 시 초기 상태 업데이트)
  const handleSaveTypoDraftWithUpdate = useCallback(async () => {
    await onSaveTypoDraft?.();
    // 저장 성공 시 초기 상태 업데이트
    typoInitialStateRef.current = {
      selectedTypos: JSON.parse(JSON.stringify(selectedSlideTypos || {})),
      editedSuggestions: JSON.parse(JSON.stringify(editedSuggestions || {}))
    };
  }, [onSaveTypoDraft, selectedSlideTypos, editedSuggestions]);

  // 슬라이드 번호 클릭 시 입력 모드로 전환
  const handleSlideNumberClick = useCallback(() => {
    setIsEditingSlideNumber(true);
    setSlideInputValue(String(currentSlideIndex + 1));
    setSlideInputError('');
  }, [currentSlideIndex]);

  // 슬라이드 번호 입력 처리
  const handleSlideInputChange = useCallback((e) => {
    setSlideInputValue(e.target.value);
    setSlideInputError('');
  }, []);

  // 슬라이드 번호 입력 확정 (Enter 또는 blur)
  const handleSlideInputConfirm = useCallback(() => {
    const inputNum = parseInt(slideInputValue, 10);

    if (isNaN(inputNum) || inputNum < 1 || inputNum > slides.length) {
      setSlideInputError(t('thumbnailNav.invalidSlideNumber'));
      return;
    }

    goToSlide(inputNum - 1);
    setIsEditingSlideNumber(false);
    setSlideInputError('');
  }, [slideInputValue, slides.length, goToSlide, t]);

  // Enter 키 처리
  const handleSlideInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSlideInputConfirm();
    } else if (e.key === 'Escape') {
      setIsEditingSlideNumber(false);
      setSlideInputError('');
    }
  }, [handleSlideInputConfirm]);

  // 타입별 설정
  const config = {
    typo: {
      title: t('typoViewer.title'),
      buttonText: t('typoViewer.checkSlide'),
      checkingText: t('typoViewer.checking'),
      emptyMessage: t('typoViewer.emptyMessage'),
      icon: FaSpellCheck
    },
    translate: {
      title: t('translateViewer.title'),
      buttonText: t('translateViewer.translatePpt'),
      checkingText: t('translateViewer.translating'),
      emptyMessage: t('translateViewer.emptyMessage'),
      icon: FaLanguage
    }
  };

  const currentConfig = config[tabType] || config.typo;
  const IconComponent = currentConfig.icon;

  if (!slides || !Array.isArray(slides) || slides.length === 0) {
    return (
      <div style={{
        padding: '20%',
        background: '#f5f7fa',
        // minHeight: '100vh',
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '24px',
          padding: '48px 40px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '56px',
            marginBottom: '20px'
          }}>
            📄
          </div>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '12px',
            letterSpacing: '-0.01em'
          }}>
            {t('typoViewer.selectPpt')}
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#6b7280',
            fontWeight: '500',
            lineHeight: '1.6'
          }}>
            {currentConfig.emptyMessage}
          </p>
        </div>
      </div>
    );
  }

  // 언어 목록 (config에서 가져옴)
  const languageOptions = SUPPORTED_LANGUAGES;

  // 번역 모드일 때 별도 레이아웃
  if (!isTypoMode) {
    return (
      <div style={{
        padding: isMobile ? '12px' : '24px',
        background: '#f5f7fa',
        minHeight: '100vh',
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}>
        {/* CSS 애니메이션 */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `
        }} />

        {/* 언어 선택 모달 */}
        {showLanguageModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => setShowLanguageModal(false)}>
            <div style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                {t('translateViewer.selectLanguage')}
              </h3>

              {/* 라디오 버튼 목록 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '24px',
                maxHeight: '40vh',
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
                {languageOptions.map(lang => (
                  <label
                    key={lang.code}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: selectedLanguage === lang.code ? '2px solid #1A62EA' : '2px solid #e5e7eb',
                      background: selectedLanguage === lang.code ? '#f0f7ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="language"
                      value={lang.code}
                      checked={selectedLanguage === lang.code}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: '#1A62EA'
                      }}
                    />
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      [{lang.code.toUpperCase()}] {lang.name}
                    </span>
                  </label>
                ))}
              </div>

              {/* 버튼 영역 */}
              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button
                  onClick={() => setShowLanguageModal(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    background: '#ffffff',
                    color: '#6b7280',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowLanguageModal(false);
                    translatePPT && translatePPT(selectedLanguage);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#1A62EA',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('translateViewer.translate')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 메인 컨테이너 - PC: 좌우(4:1), 모바일: 상하 */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '24px',
          maxWidth: isMobile ? '100%' : '1600px',
          margin: '0 auto',
          height: isMobile ? 'auto' : (slides.length <= 10 ? '72vh' : '66vh'),
          width: '100%'
        }}>
          {/* 슬라이드 뷰어 (PC: flex 4, 모바일: 전체 너비) */}
          <div style={{
            flex: isMobile ? 'none' : '4',
            width: isMobile ? '100%' : 'auto',
            background: '#ffffff',
            borderRadius: '24px',
            padding: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e5e7eb',
            height: isMobile ? 'auto' : '100%',
            overflow: 'hidden'
          }}>
            {/* 진행률 표시 */}
            {isTranslating && translateProgress && (
              <div style={{
                textAlign: 'center',
                marginBottom: '16px',
                padding: '12px',
                background: '#f0fdf4',
                borderRadius: '12px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#166534',
                  marginBottom: '4px'
                }}>
                  {translateProgress.status || t('translateViewer.translating')}
                </div>
                {translateProgress.total > 0 && (
                  <div style={{
                    fontSize: '12px',
                    color: '#15803d',
                    fontWeight: '500'
                  }}>
                    {t('typoViewer.progressSlides', { current: translateProgress.current, total: translateProgress.total })}
                  </div>
                )}
              </div>
            )}

            {/* 슬라이드 정보 */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
              marginTop: '24px',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  {t('typoViewer.slideInfo', { current: '', total: '' }).split(' ')[0]}{' '}
                  {isEditingSlideNumber ? (
                    <input
                      type="number"
                      value={slideInputValue}
                      onChange={handleSlideInputChange}
                      onBlur={handleSlideInputConfirm}
                      onKeyDown={handleSlideInputKeyDown}
                      autoFocus
                      min={1}
                      max={slides.length}
                      style={{
                        width: '80px',
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#1A62EA',
                        textAlign: 'center',
                        border: '2px solid #1A62EA',
                        borderRadius: '8px',
                        outline: 'none',
                        padding: '4px 8px',
                        background: '#EBF4FF'
                      }}
                    />
                  ) : (
                    <span
                      onClick={handleSlideNumberClick}
                      style={{
                        cursor: 'pointer',
                        color: '#1A62EA',
                        fontSize: '24px',
                        fontWeight: '700',
                        padding: '4px 12px',
                        border: '1px solid #cbd5e0',
                        borderRadius: '8px',
                        background: '#f7fafc',
                        transition: 'all 0.2s ease'
                      }}
                      title={t('thumbnailNav.clickToEdit')}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#EBF4FF';
                        e.target.style.borderColor = '#1A62EA';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f7fafc';
                        e.target.style.borderColor = '#cbd5e0';
                      }}
                    >
                      {currentSlideIndex + 1}
                    </span>
                  )}
                  {' / '}{slides.length}
                </span>
                {slideInputError && (
                  <span style={{
                    fontSize: '12px',
                    color: '#ef4444'
                  }}>
                    {slideInputError}
                  </span>
                )}
              </div>
            </div>

            {/* 슬라이드 네비게이션 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '16px',
              marginBottom: '24px'
            }}>
              {/* 이전 버튼 */}
              <button
                onClick={goToPrevSlide}
                disabled={currentSlideIndex === 0}
                style={{
                  background: currentSlideIndex === 0 ? '#d1d5db' : '#1A62EA',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: isMobile ? '28px' : '48px',
                  height: isMobile ? '28px' : '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: currentSlideIndex === 0 ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '12px' : '18px',
                  transition: 'all 0.2s ease',
                  opacity: currentSlideIndex === 0 ? 0.5 : 1,
                  flexShrink: 0
                }}
              >
                <FaChevronLeft />
              </button>

              {/* 슬라이드 썸네일 */}
              <div style={{
                background: '#fff',
                borderRadius: isMobile ? '12px' : '16px',
                padding: isMobile ? '8px' : '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                width: isMobile ? '100%' : '84%',
                maxHeight: isMobile ? '40vh' : '53vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: isMobile ? 1 : 'none'
              }}>
                {slides[currentSlideIndex] && slides[currentSlideIndex].thumbnail_url ? (
                  <img
                    src={slides[currentSlideIndex].thumbnail_url}
                    alt={`slide-${currentSlideIndex + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    paddingTop: '66.67%',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    color: '#6c757d',
                    background: '#f3f4f6',
                    borderRadius: '8px'
                  }}>
                    {t('typoViewer.slideImageError')}
                  </div>
                )}
              </div>

              {/* 다음 버튼 */}
              <button
                onClick={goToNextSlide}
                disabled={currentSlideIndex === slides.length - 1}
                style={{
                  background: currentSlideIndex === slides.length - 1 ? '#d1d5db' : '#1A62EA',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: isMobile ? '28px' : '48px',
                  height: isMobile ? '28px' : '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: currentSlideIndex === slides.length - 1 ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '12px' : '18px',
                  transition: 'all 0.2s ease',
                  opacity: currentSlideIndex === slides.length - 1 ? 0.5 : 1,
                  flexShrink: 0
                }}
              >
                <FaChevronRight />
              </button>
            </div>

            {/* 슬라이드 인디케이터 - 10개 이하일 때만 표시 */}
            {slides.length <= 10 && (
              <div style={{
                paddingTop: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                flexWrap: 'wrap'
              }}>
                {slides.map((_, index) => (
                  <div
                    key={index}
                    onClick={() => goToSlide(index)}
                    style={{
                      width: index === currentSlideIndex ? '24px' : '10px',
                      height: '10px',
                      borderRadius: '5px',
                      background: index === currentSlideIndex ? '#1A62EA' : '#d1d5db',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    title={t('typoViewer.goToSlide', { num: index + 1 })}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 번역 완료된 언어 목록 (PC: flex 1, 모바일: 전체 너비) */}
          <div style={{
            flex: isMobile ? 'none' : '1',
            width: isMobile ? '100%' : 'auto',
            background: '#ffffff',
            borderRadius: '24px',
            padding: isMobile ? '16px' : '20px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e5e7eb',
            minWidth: isMobile ? 'auto' : '200px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: isMobile ? '300px' : 'none'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '5px',
              textAlign: 'center',
              paddingBottom: '12px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0
            }}>
              {t('translateViewer.translatedLanguages')}
            </h3>

            {/* 번역된 언어 카드 목록 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              overflowY: 'auto',
              flex: 1
            }}>
              {translatedLanguages && Object.keys(translatedLanguages).length > 0 ? (
                Object.keys(translatedLanguages)
                  .sort((a, b) => {
                    const aTime = translatedLanguages[a]?.completed_at;
                    const bTime = translatedLanguages[b]?.completed_at;
                    if (!aTime) return 1;
                    if (!bTime) return -1;
                    const aDate = aTime.toDate ? aTime.toDate() : new Date(aTime);
                    const bDate = bTime.toDate ? bTime.toDate() : new Date(bTime);
                    return bDate - aDate; // 최신순 (내림차순)
                  })
                  .map(lang => {
                  const langInfo = languageOptions.find(l => l.code === lang) || { name: lang, code: lang, englishName: lang };
                  return (
                    <div
                      key={lang}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '16px',
                        padding: '20px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {/* 언어 코드 + 언어명 */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        <div>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1f2937'
                          }}>
                            [{langInfo.code.toUpperCase()}] {langInfo.name}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            {langInfo.englishName}
                          </div>
                        </div>
                      </div>

                      {/* 상태에 따른 버튼 */}
                      {(() => {
                        const langData = translatedLanguages[lang];
                        const status = langData?.status || 'completed';

                        // 번역 진행 중
                        if (status === 'translating') {
                          return (
                            <button
                              disabled
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                background: '#9ca3af',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              {/* 스피너 */}
                              <svg
                                style={{
                                  animation: 'spin 1s linear infinite',
                                  width: '16px',
                                  height: '16px'
                                }}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                              </svg>
                              {t('translateViewer.preparing') || '준비 중...'}
                            </button>
                          );
                        }

                        // 에러 발생 - 재시도 버튼
                        if (status === 'error') {
                          return (
                            <button
                              onClick={() => translatePPT && translatePPT(lang)}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                background: '#ef4444',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#dc2626';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#ef4444';
                              }}
                            >
                              <FaRedo size={14} />
                              {t('translateViewer.retry') || '재시도'}
                            </button>
                          );
                        }

                        // 완료 - 다운로드 버튼
                        return (
                          <button
                            onClick={() => downloadTranslatedPPT && downloadTranslatedPPT(lang)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              background: '#1A62EA',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#1554c7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#1A62EA';
                            }}
                          >
                            <FaDownload size={14} />
                            {t('translateViewer.downloadPpt')}
                          </button>
                        );
                      })()}
                    </div>
                  );
                })
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '24px 16px',
                  color: '#9ca3af',
                  fontSize: '13px'
                }}>
                  {t('translateViewer.noTranslations')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PPT 번역 버튼 - 우측 하단 고정 */}
        <button
          onClick={() => setShowLanguageModal(true)}
          disabled={isTranslating}
          style={{
            position: 'fixed',
            bottom: isMobile ? '16px' : '32px',
            right: isMobile ? '16px' : '32px',
            background: isTranslating ? '#9ca3af' : '#1A62EA',
            color: 'white',
            border: 'none',
            borderRadius: isMobile ? '12px' : '16px',
            padding: isMobile ? '12px 16px' : '16px 24px',
            cursor: isTranslating ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '10px',
            boxShadow: isTranslating
              ? '0 4px 15px rgba(156, 163, 175, 0.4)'
              : '0 8px 24px rgba(26, 98, 234, 0.4)',
            transition: 'all 0.2s ease',
            zIndex: 100
          }}
          onMouseEnter={(e) => {
            if (!isTranslating) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(26, 98, 234, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isTranslating) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 98, 234, 0.4)';
            }
          }}
        >
          {isTranslating ? (
            <>
              <span style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></span>
              {translateProgress?.status || t('translateViewer.translating')}
            </>
          ) : (
            <>
              {t('translateViewer.translatePpt')}
            </>
          )}
        </button>
      </div>
    );
  }

  // 오타 모드 (새로운 2컬럼 레이아웃)
  return (
    <div style={{
      padding: isMobile ? '12px' : '24px',
      background: '#f8f9fa',
      minHeight: '100vh',
      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden'
    }}>
      {/* CSS 애니메이션 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />

      {/* 메인 컨테이너 - 2컬럼 레이아웃 */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '24px',
        maxWidth: isMobile ? '100%' : '1600px',
        margin: '0 auto',
        minHeight: isMobile ? 'auto' : (slides.length <= 10 ? '76vh' : '72vh'),
        width: '100%'
      }}>
        {/* 왼쪽: 슬라이드 뷰어 */}
        <div style={{
          flex: isMobile ? 'none' : '3',
          width: isMobile ? '100%' : 'auto',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: isMobile ? '16px' : '24px',
          padding: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb'
        }}>
          {/* 진행률 표시 - 버튼에서만 표시하므로 주석처리 */}
          {/* {isTypoAnalyzingAll && checkAllProgress.status && (
            <div style={{
              textAlign: 'center',
              marginBottom: '16px',
              padding: '12px',
              background: 'linear-gradient(145deg, rgba(240, 253, 244, 0.8) 0%, rgba(220, 252, 231, 0.8) 100%)',
              borderRadius: '12px',
              border: '1px solid rgba(74, 222, 128, 0.3)'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#166534'
              }}>
                {checkAllProgress.status}
              </div>
            </div>
          )} */}

          {/* 슬라이드 정보 */}
          <div style={{
            textAlign: 'center',
            marginTop: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px'
              }}>
                <span style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  {t('typoViewer.slideInfo', { current: '', total: '' }).split(' ')[0]}{' '}
                  {isEditingSlideNumber ? (
                    <input
                      type="number"
                      value={slideInputValue}
                      onChange={handleSlideInputChange}
                      onBlur={handleSlideInputConfirm}
                      onKeyDown={handleSlideInputKeyDown}
                      autoFocus
                      min={1}
                      max={slides.length}
                      style={{
                        width: '80px',
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#1A62EA',
                        textAlign: 'center',
                        border: '2px solid #1A62EA',
                        borderRadius: '8px',
                        outline: 'none',
                        padding: '4px 8px',
                        background: '#EBF4FF'
                      }}
                    />
                  ) : (
                    <span
                      onClick={handleSlideNumberClick}
                      style={{
                        cursor: 'pointer',
                        color: '#1A62EA',
                        fontSize: '24px',
                        fontWeight: '700',
                        padding: '4px 12px',
                        border: '1px solid #cbd5e0',
                        borderRadius: '8px',
                        background: '#f7fafc',
                        transition: 'all 0.2s ease'
                      }}
                      title={t('thumbnailNav.clickToEdit')}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#EBF4FF';
                        e.target.style.borderColor = '#1A62EA';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f7fafc';
                        e.target.style.borderColor = '#cbd5e0';
                      }}
                    >
                      {currentSlideIndex + 1}
                    </span>
                  )}
                  {' / '}{slides.length}
                </span>
                {isSlideLoading && (
                  <span style={{
                    fontSize: '14px',
                    color: '#ffffff',
                    background: '#1A62EA',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    {t('typoViewer.loading')}
                  </span>
                )}
              </div>
              {slideInputError && (
                <span style={{
                  fontSize: '12px',
                  color: '#ef4444'
                }}>
                  {slideInputError}
                </span>
              )}
            </div>
          </div>

          {/* 슬라이드 네비게이션 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '16px',
            marginBottom: '24px'
          }}>
            {/* 이전 버튼 */}
            <button
              onClick={goToPrevSlide}
              disabled={currentSlideIndex === 0}
              style={{
                background: currentSlideIndex === 0 ? '#d1d5db' : '#1A62EA',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: isMobile ? '28px' : '48px',
                height: isMobile ? '28px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: currentSlideIndex === 0 ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '12px' : '18px',
                transition: 'all 0.2s ease',
                opacity: currentSlideIndex === 0 ? 0.5 : 1,
                flexShrink: 0
              }}
            >
              <FaChevronLeft />
            </button>

            {/* 슬라이드 썸네일 */}
            <div style={{
              background: '#fff',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '8px' : '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              width: isMobile ? 'calc(100% - 72px)' : '50vw',
              maxWidth: isMobile ? '100%' : 'none',
              maxHeight: isMobile ? '35vh' : '53vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: isMobile ? 1 : 'none'
            }}>
              {slides[currentSlideIndex] && slides[currentSlideIndex].thumbnail_url ? (
                <img
                  src={slides[currentSlideIndex].thumbnail_url}
                  alt={`slide-${currentSlideIndex + 1}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    display: 'block'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  paddingTop: '66.67%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  color: '#6c757d',
                  background: '#f3f4f6',
                  borderRadius: '8px'
                }}>
                  {t('typoViewer.slideImageError')}
                </div>
              )}
            </div>

            {/* 다음 버튼 */}
            <button
              onClick={goToNextSlide}
              disabled={currentSlideIndex === slides.length - 1}
              style={{
                background: currentSlideIndex === slides.length - 1 ? '#d1d5db' : '#1A62EA',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: isMobile ? '28px' : '48px',
                height: isMobile ? '28px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: currentSlideIndex === slides.length - 1 ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '12px' : '18px',
                transition: 'all 0.2s ease',
                opacity: currentSlideIndex === slides.length - 1 ? 0.5 : 1,
                flexShrink: 0
              }}
            >
              <FaChevronRight />
            </button>
          </div>

          {/* 이 슬라이드 오타검사 버튼 */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => checkSlideTypos(currentSlideIndex)}
              disabled={isTypoAnalyzingAll || slideTypoCheckStatus[currentSlideIndex]?.checking || !slides[currentSlideIndex]?.hasText}
              style={{
                background: (isTypoAnalyzingAll || slideTypoCheckStatus[currentSlideIndex]?.checking) ? '#9ca3af' : '#1A62EA',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 28px',
                cursor: (isTypoAnalyzingAll || slideTypoCheckStatus[currentSlideIndex]?.checking || !slides[currentSlideIndex]?.hasText) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                opacity: (!slides[currentSlideIndex]?.hasText && !isTypoAnalyzingAll && !slideTypoCheckStatus[currentSlideIndex]?.checking) ? 0.5 : 1
              }}
            >
              <IconComponent />
              {slideTypoCheckStatus[currentSlideIndex]?.checking ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  {currentConfig.checkingText}
                </>
              ) : (
                currentConfig.buttonText
              )}
            </button>
          </div>

          {/* 슬라이드 인디케이터 - 10개 이하일 때만 표시 */}
          {slides.length <= 10 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '6px',
              marginTop: '16px',
              flexWrap: 'wrap'
            }}>
              {slides.map((_, index) => (
                <div
                  key={index}
                  onClick={() => goToSlide(index)}
                  style={{
                    width: index === currentSlideIndex ? '24px' : '10px',
                    height: '10px',
                    borderRadius: '5px',
                    background: index === currentSlideIndex ? '#1A62EA' : '#d1d5db',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  title={t('typoViewer.goToSlide', { num: index + 1 })}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* 오타 검사 결과 모달 */}
      <TypoResultModal
        isOpen={showTypoResultModal}
        onClose={handleCloseTypoResultModal}
        pdfUrl={pdfUrl}
        pdfDoc={pdfDoc}
        setPdfDoc={setPdfDoc}
        slides={slides}
        slideTypoResults={slideTypoResults}
        currentSlideIndex={currentSlideIndex}
        goToSlide={goToSlide}
        selectedSlideTypos={selectedSlideTypos}
        toggleSlideTypoSelection={toggleSlideTypoSelection}
        updateEditedSuggestion={updateEditedSuggestion}
        getEditedSuggestion={getEditedSuggestion}
        editedSuggestions={editedSuggestions}
        applySlideTypoCorrections={applySlideTypoCorrections}
        applyingTypoCorrections={applyingTypoCorrections}
        isMobile={isMobile}
        selectedPpt={selectedPpt}
        slideSize={typoSlideSize}
        onSaveDraft={handleSaveTypoDraftWithUpdate}
        checkSlideTypos={checkSlideTypos}
        slideTypoCheckStatus={slideTypoCheckStatus}
        initialState={typoInitialStateRef.current}
      />

      {/* 우측 하단 고정 버튼들 */}
      <div style={{
        position: 'fixed',
        bottom: isMobile ? '16px' : '32px',
        right: isMobile ? '16px' : '32px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '12px',
        zIndex: 100
      }}>
        {/* 확인 버튼 - 오타검사 결과가 있을 때만 표시 */}
        {Object.keys(slideTypoResults).length > 0 && (
          <button
            onClick={handleOpenTypoResultModal}
            style={{
              background: '#ffffff',
              color: '#1A62EA',
              border: '2px solid #1A62EA',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '12px 16px' : '16px 24px',
              cursor: 'pointer',
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '6px' : '10px',
              boxShadow: '0 8px 24px rgba(26, 98, 234, 0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(26, 98, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 98, 234, 0.2)';
            }}
          >
          {t('typoViewer.viewResults')}
          </button>
        )}

        {/* 전체 슬라이드 오타검사 버튼 */}
        <button
          onClick={() => checkAllSlidesTypos && checkAllSlidesTypos()}
          disabled={isTypoAnalyzingAll || isTypoAnySingleSlideChecking}
          style={{
            background: (isTypoAnalyzingAll || isTypoAnySingleSlideChecking) ? '#9ca3af' : '#1A62EA',
            color: 'white',
            border: 'none',
            borderRadius: isMobile ? '12px' : '16px',
            padding: isMobile ? '12px 16px' : '16px 24px',
            cursor: (isTypoAnalyzingAll || isTypoAnySingleSlideChecking) ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '10px',
            boxShadow: '0 8px 24px rgba(26, 98, 234, 0.4)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!isTypoAnalyzingAll && !isTypoAnySingleSlideChecking) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(26, 98, 234, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isTypoAnalyzingAll && !isTypoAnySingleSlideChecking) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 98, 234, 0.4)';
            }
          }}
        >
          {isTypoAnalyzingAll ? (
            <>
              <span style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></span>
              {getProgressMessage(t, checkAllProgress.status, checkAllProgress.percent)}
            </>
          ) : (
            t('typoViewer.checkAllSlides')
          )}
        </button>
      </div>
    </div>
  );
};

export default TypoTranslateViewer;